import { Eye, EyeOff, Lock, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, isAbortError, type RevealResponse, type SubjectIdentifier, type WriteOptions } from '../api';
import { newIdempotencyKey } from '../lib/ids';
import { WriteError } from './Write';

/**
 * An identity number (CCCD, passport, business registration...) shown only as the
 * server's masked hint. The operator can reveal it with a reason through
 * `POST /admin/v1/subjects/identifiers/{id}/reveal`, which is the audit point.
 * The cleartext lives only in this component's state and is re-masked after
 * `expires_in_seconds`, on "Hide", or on unmount. It is never cached or stored.
 */

/** An enterprise tax code the server shows whole (personal identifiers always come masked). */
function isPublicIdentifier(identifier: Pick<SubjectIdentifier, 'type' | 'hint'>): boolean {
  return (identifier.type === 'TAX_CODE' || identifier.type === 'ENTERPRISE_CODE') && Boolean(identifier.hint) && !/[*•]/.test(identifier.hint ?? '');
}

/** Reason codes; the audited reason sent to the API stays in English whatever the UI language. */
const REASONS = {
  support: 'Customer support request',
  vnnic: 'VNNIC / compliance verification',
  legal: 'Legal or law-enforcement request',
  abuse: 'Abuse or fraud investigation',
} as const;
type ReasonKey = keyof typeof REASONS;

type Phase = 'masked' | 'confirming' | 'revealing' | 'revealed';

export function RevealIdentifier({
  identifier,
  revealer,
  formatValue,
}: {
  identifier: SubjectIdentifier;
  /** Another audited reveal endpoint (e.g. the birth date); default: the identifier reveal. */
  revealer?: (reason: string, opts: WriteOptions) => Promise<RevealResponse>;
  /** Display form of the revealed value. */
  formatValue?: (value: string) => string;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('masked');
  const [reason, setReason] = useState<ReasonKey>('support');
  const [ticket, setTicket] = useState('');
  const [value, setValue] = useState<{ text: string; expiresAt: number } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const controller = useRef<AbortController | null>(null);
  /** One Idempotency-Key per distinct reveal request; reused only for an identical retry. */
  const pending = useRef<{ key: string; reason: string } | null>(null);
  const formId = useId();
  const { t: td } = useTranslation('domains');
  const fallback = identifier.type.replace(/_/g, ' ').toLowerCase();
  const label = (td as unknown as (key: string, o: { defaultValue: string }) => string)(`subject.identifierTypes.${identifier.type.toUpperCase()}`, {
    defaultValue: fallback,
  });

  const hide = useCallback(() => {
    controller.current?.abort();
    pending.current = null;
    setValue(null);
    setError(null);
    setPhase('masked');
  }, []);

  useEffect(() => {
    if (!value) return undefined;
    const timer = window.setTimeout(hide, Math.max(0, value.expiresAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [value, hide]);

  useEffect(() => () => controller.current?.abort(), []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const base = REASONS[reason];
    const fullReason = ticket.trim() ? `${base} (ref: ${ticket.trim()})` : base;
    if (pending.current?.reason !== fullReason) pending.current = { key: newIdempotencyKey(), reason: fullReason };
    controller.current?.abort();
    const c = new AbortController();
    controller.current = c;
    setError(null);
    setPhase('revealing');
    try {
      const opts = { idempotencyKey: pending.current.key, signal: c.signal };
      const res = revealer ? await revealer(fullReason, opts) : await api.revealSubjectIdentifier(identifier.id, fullReason, opts);
      pending.current = null;
      setValue({ text: formatValue ? formatValue(res.value) : res.value, expiresAt: Date.now() + res.expires_in_seconds * 1000 });
      setPhase('revealed');
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err);
      setPhase('confirming');
    }
  };

  // An enterprise's tax code is public business-registry data: shown as it is, nothing to reveal.
  if (isPublicIdentifier(identifier)) {
    return <code className="rounded bg-muted px-2 py-0.5 font-mono text-sm tracking-wider">{identifier.hint}</code>;
  }

  if (phase === 'revealed' && value) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <code className="rounded border border-warning/40 bg-warning-soft px-2 py-0.5 font-mono text-sm">{value.text}</code>
        <Button type="button" variant="outline" size="xs" onClick={hide}>
          <EyeOff /> {t('reveal.hide')}
        </Button>
        <span className="text-xs text-muted-foreground">{t('reveal.revealedNote')}</span>
      </div>
    );
  }

  const busy = phase === 'revealing';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <code className="rounded bg-muted px-2 py-0.5 font-mono text-sm tracking-wider" aria-label={t('reveal.hidden', { label })}>
        {identifier.hint ?? '••••••••'}
      </code>
      <span
        className="inline-flex items-center gap-1 rounded-md border border-warning/30 bg-warning-soft px-1.5 py-0.5 text-xs font-medium text-warning"
        title={t('reveal.sensitiveHint')}
      >
        <Lock className="size-3" aria-hidden="true" /> {t('reveal.sensitive')}
      </span>
      <Button type="button" variant="outline" size="xs" onClick={() => setPhase('confirming')}>
        <Eye /> {t('reveal.reveal')}
      </Button>
      <Dialog
        open={phase === 'confirming' || phase === 'revealing'}
        onOpenChange={(open) => {
          if (!open && !busy) hide();
        }}
      >
        <DialogContent>
          <form onSubmit={submit} className="grid gap-4" aria-labelledby={`${formId}-title`}>
            <DialogHeader>
              <DialogTitle id={`${formId}-title`} className="flex items-center gap-2">
                <ShieldAlert className="size-5 text-warning" aria-hidden="true" /> {t('reveal.dialogTitle')}
              </DialogTitle>
              <DialogDescription>{t('reveal.dialogBody', { label })}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-reason`}>{t('reveal.reasonLabel')}</Label>
              <Select value={reason} onValueChange={(v) => setReason(v as ReasonKey)} disabled={busy}>
                <SelectTrigger id={`${formId}-reason`} className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(REASONS) as ReasonKey[]).map((k) => (
                    <SelectItem key={k} value={k}>
                      {t(`reveal.reasons.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-ticket`}>{t('reveal.ticket')}</Label>
              <Input
                id={`${formId}-ticket`}
                type="text"
                value={ticket}
                onChange={(e) => setTicket(e.target.value)}
                disabled={busy}
                autoComplete="off"
                maxLength={64}
                placeholder={t('reveal.ticketPlaceholder')}
              />
            </div>
            {error ? <WriteError error={error} /> : null}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={hide} disabled={busy}>
                {t('action.cancel')}
              </Button>
              <Button type="submit" disabled={busy}>
                <Eye /> {busy ? t('reveal.revealing') : t('reveal.confirm')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
